import { useEffect, useState } from "react";
import { Link, useLocation, Outlet } from "react-router-dom";
import { motion } from "framer-motion";
import {
  LayoutDashboard,
  TrendingUp,
  Users,
  History,
  LogOut,
  ChevronLeft,
  Phone,
  Smartphone,
  UserPlus,
  BarChart3,
  Globe,
  Presentation,
  Gauge,
  MapPin,
  Menu,
  X,
} from "lucide-react";
import { useOverdueFollowUps } from "@/hooks/use-sales";
import mynaLogo from "@/assets/myna-logo.svg";
import { ThemeToggle } from "@/components/admin/ThemeToggle";

/**
 * SALES PORTAL LAYOUT - Washed Charcoal Premium Aesthetic
 *
 * Matches marketing site design system:
 * - Background: #292929 (main), #2d2d2d, #2c2c2c (surfaces)
 * - Borders: white/[0.08], white/[0.05]
 * - Text: gray-100 (headings), gray-400 (body), gray-500 (labels)
 * - Accents: Lime #84CC16, Teal #14B8A6 at 70-80% opacity
 * - Sharp corners, premium shadows
 *
 * Responsive: desktop sidebar (lg+), mobile topbar + slide-out drawer (<lg).
 */

const navItems = [
  { title: "Dashboard", href: "/sales", icon: LayoutDashboard },
  { title: "Active Demos", href: "/sales/demos", icon: Phone },
  { title: "Websites", href: "/sales/websites", icon: Globe },
  { title: "Onboarding", href: "/sales/onboarding/queue", icon: UserPlus },
  { title: "Pipeline", href: "/sales/pipeline", icon: TrendingUp },
  { title: "Prospects", href: "/sales/prospects", icon: Users },
  { title: "Phone Pool", href: "/sales/pool", icon: Smartphone },
  { title: "Brokers", href: "/sales/brokers", icon: Gauge },
  { title: "Broker Prospecting", href: "/sales/broker-prospects", icon: MapPin },
  { title: "Presentation", href: "/sales/deck", icon: Presentation },
  { title: "Analytics", href: "/sales/analytics", icon: BarChart3 },
  { title: "History", href: "/sales/history", icon: History },
];

export function SalesLayout() {
  const location = useLocation();
  const { data: overdueFollowUps } = useOverdueFollowUps();
  const overdueCount = overdueFollowUps?.length || 0;
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (drawerOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [drawerOpen]);

  const isItemActive = (href: string) => {
    const isOnboardingActive =
      href === "/sales/onboarding/queue" &&
      location.pathname.startsWith("/sales/onboarding/");
    return (
      location.pathname === href ||
      isOnboardingActive ||
      (href !== "/sales" &&
        href !== "/sales/demos/new" &&
        !href.includes("/onboarding/") &&
        location.pathname.startsWith(href) &&
        !location.pathname.endsWith("/new"))
    );
  };

  const renderNavItems = () =>
    navItems.map((item, index) => {
      const isActive = isItemActive(item.href);
      const accentColor = index % 2 === 0 ? "lime" : "teal";

      return (
        <Link
          key={item.href}
          to={item.href}
          className={`flex items-center gap-3 px-4 py-3 transition-all border-l-2 group ${
            isActive
              ? accentColor === "lime"
                ? "bg-lime-500/10 text-lime-400 border-lime-500/60 shadow-lg shadow-lime-500/10"
                : "bg-teal-500/10 text-teal-400 border-teal-500/60 shadow-lg shadow-teal-500/10"
              : "text-gray-400 hover:text-gray-200 hover:bg-white/5 border-transparent hover:border-white/10"
          }`}
        >
          <item.icon
            className={`w-5 h-5 transition-transform group-hover:scale-110 ${
              isActive
                ? accentColor === "lime"
                  ? "text-lime-400"
                  : "text-teal-400"
                : "text-gray-500"
            }`}
            strokeWidth={isActive ? 2 : 1.5}
          />
          <span className="font-semibold text-sm tracking-wide">{item.title}</span>
          {item.title === "Pipeline" && overdueCount > 0 && (
            <span className="ml-auto flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[10px] font-bold bg-red-500 text-white rounded-full animate-pulse">
              {overdueCount}
            </span>
          )}
        </Link>
      );
    });

  const sidebarInner = (
    <>
      {/* Logo */}
      <div className="p-6 border-b border-white/[0.08] flex items-start justify-between">
        <Link to="/sales" className="block">
          <img src={mynaLogo} alt="MYNA AI" className="h-20 lg:h-24 w-auto opacity-90 mb-2" />
          <div className="px-2 py-1 bg-lime-500/10 border border-lime-500/30 inline-block">
            <span className="text-xs text-lime-400/80 font-semibold tracking-wider uppercase">
              Sales Portal
            </span>
          </div>
        </Link>
        <button
          type="button"
          onClick={() => setDrawerOpen(false)}
          className="lg:hidden p-2 -mr-2 text-gray-400 hover:text-gray-200 transition-colors"
          aria-label="Close menu"
        >
          <X className="w-5 h-5" strokeWidth={1.5} />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">{renderNavItems()}</nav>

      {/* Footer */}
      <div className="p-4 border-t border-white/[0.08] space-y-2">
        <ThemeToggle />
        <Link
          to="/"
          className="flex items-center gap-3 px-4 py-3 text-gray-400 hover:text-gray-200 hover:bg-white/5 transition-all group"
        >
          <ChevronLeft className="w-5 h-5 text-gray-500 group-hover:text-teal-400 transition-colors" strokeWidth={1.5} />
          <span className="font-medium text-sm">Back to Website</span>
        </Link>
        <Link
          to="/login"
          className="flex items-center gap-3 px-4 py-3 text-gray-400 hover:text-gray-200 hover:bg-white/5 transition-all group"
        >
          <LogOut className="w-5 h-5 text-gray-500 group-hover:text-red-400 transition-colors" strokeWidth={1.5} />
          <span className="font-medium text-sm">Sign Out</span>
        </Link>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-[#292929] text-white flex">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[100] focus:px-4 focus:py-2 focus:bg-lime-500 focus:text-[#292929] focus:font-semibold focus:rounded-md">
        Skip to main content
      </a>
      {/* Gradient wash background */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-b from-[#2e2e2e] via-[#292929] to-[#282828]" />
        <div
          className="absolute top-1/4 left-1/4 w-[600px] h-[600px]"
          style={{
            background: "radial-gradient(circle, rgba(132,204,22,0.015) 0%, transparent 70%)",
            filter: "blur(120px)",
          }}
        />
      </div>

      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex relative z-10 w-64 bg-[#2d2d2d] border-r border-white/[0.08] flex-col shadow-2xl">
        {sidebarInner}
      </aside>

      {/* Mobile Drawer + Backdrop */}
      <div
        className={`lg:hidden fixed inset-0 z-40 transition-opacity duration-200 ${
          drawerOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        onClick={() => setDrawerOpen(false)}
        aria-hidden={!drawerOpen}
      >
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      </div>
      <aside
        className={`lg:hidden fixed inset-y-0 left-0 z-50 w-72 max-w-[85vw] bg-[#2d2d2d] border-r border-white/[0.08] flex flex-col shadow-2xl transform transition-transform duration-200 ease-out ${
          drawerOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        aria-hidden={!drawerOpen}
      >
        {sidebarInner}
      </aside>

      {/* Main Content */}
      <main id="main-content" className="relative z-10 flex-1 overflow-auto min-w-0">
        {/* Mobile Topbar */}
        <div className="lg:hidden sticky top-0 z-30 flex items-center justify-between gap-3 px-4 py-3 bg-[#2d2d2d]/95 backdrop-blur border-b border-white/[0.08]">
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            className="relative p-2 -ml-2 text-gray-300 hover:text-gray-100 transition-colors"
            aria-label="Open menu"
          >
            <Menu className="w-6 h-6" strokeWidth={1.5} />
            {overdueCount > 0 && (
              <span className="absolute top-0.5 right-0.5 flex items-center justify-center min-w-[16px] h-4 px-1 text-[9px] font-bold bg-red-500 text-white rounded-full">
                {overdueCount}
              </span>
            )}
          </button>
          <Link to="/sales" className="flex items-center gap-2">
            <img src={mynaLogo} alt="MYNA AI" className="h-8 w-auto opacity-90" />
            <span className="px-2 py-0.5 bg-lime-500/10 border border-lime-500/30 text-[10px] text-lime-400/80 font-semibold tracking-wider uppercase">
              Sales
            </span>
          </Link>
          <div className="w-10" aria-hidden="true" />
        </div>

        <div className="max-w-[1600px] mx-auto p-4 sm:p-6 lg:p-8 xl:p-12">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Outlet />
          </motion.div>
        </div>
      </main>
    </div>
  );
}
